import { Box, styled } from '@mui/material';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { CommitmentType, CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column, Divider } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import api from 'lib/trpcClient';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { UpdateCapitalActionFormValues } from '../../hooks/useCapitalActionForm';

type EqualisationAllocationRow = {
  id: string;
  investorLabel: string;
  netEqualisationPayableReceivable: string;
  totalEqualisationInterestAmountLcy: string;
  totalFeesAmountLcy: string;
  capitalContributionChangeLcy: string;
  capitalContributionUsedLcy: string;
  unpaidAllocationAmountLcy: string;
};

type CapitalContributionChangeRow = {
  id: string;
  investorName: string;
  originalCapital: string;
  newCapital: string;
  change: string;
};

type EqualisationRow = {
  id: string;
  counterpartyLabel: string;
  closeDateLabel: string;
  numberOfNewCommitments: number;
  capitalContributionChange: string;
  uncalledAmount: string;
  allocationRows: Array<EqualisationAllocationRow>;
  capitalContributionChangeRows: Array<CapitalContributionChangeRow>;
};

const equalisationAllocationColumns: Array<
  TableColumn<EqualisationAllocationRow>
> = [
  { label: 'Investor', key: 'investorLabel', width: 250 },
  {
    label: 'Net equalisation payable / (receivable)',
    width: 160,
    key: 'netEqualisationPayableReceivable',
  },
  {
    label: 'Equalisation interest payable / (receivable)',
    width: 160,
    key: 'totalEqualisationInterestAmountLcy',
  },
  { label: 'Management fees', key: 'totalFeesAmountLcy', width: 160 },
  {
    label: 'Capital contribution change',
    width: 160,
    key: 'capitalContributionChangeLcy',
  },
  {
    label: 'Capital contribution used',
    width: 160,
    key: 'capitalContributionUsedLcy',
  },
  {
    label: 'Total payable / (receivable)',
    width: 160,
    key: 'unpaidAllocationAmountLcy',
  },
];

const capitalContributionChangeColumns: Array<
  TableColumn<CapitalContributionChangeRow>
> = [
  { key: 'investorName', label: 'Investor name', width: 250 },
  {
    key: 'originalCapital',
    label: 'Original capital contribution',
    width: 180,
  },
  { key: 'newCapital', label: 'New capital contribution', width: 180 },
  { key: 'change', label: 'Change', width: 180 },
];

const baseEqualisationColumns: Array<TableColumn<EqualisationRow>> = [
  { key: 'counterpartyLabel', label: 'Counterparty', width: 250 },
  { key: 'closeDateLabel', label: 'Close date', width: 140 },
  { key: 'numberOfNewCommitments', label: 'New commitments', width: 140 },
  { key: 'uncalledAmount', label: 'Uncalled amount', width: 160 },
];

const capitalAndLoanEqualisationColumns: Array<TableColumn<EqualisationRow>> = [
  { key: 'counterpartyLabel', label: 'Counterparty', width: 250 },
  { key: 'closeDateLabel', label: 'Close date', width: 140 },
  { key: 'numberOfNewCommitments', label: 'New commitments', width: 140 },
  {
    key: 'capitalContributionChange',
    label: 'Capital contribution change',
    width: 160,
  },
  { key: 'uncalledAmount', label: 'Uncalled amount', width: 160 },
];

const ExpandedContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

type Props = {
  vehicleId: string;
  index: number;
  baseCurrency: CurrencyCode;
  availableActions: Array<{
    id: string;
    ledgerDate: CalendarDay;
    uncalledAmountLcy: number;
  }>;
};

const EqualisationsDetails: React.FC<Props> = ({
  vehicleId,
  index,
  availableActions,
  baseCurrency,
}) => {
  const { vehicle } = useLayoutContext();
  const { id } = useParams<{ id?: string }>();
  const { control, watch, setValue } =
    useFormContext<UpdateCapitalActionFormValues>();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const equalisationId = watch(`entries.${index}.equalisationId`);

  const { data: actionsDetails } = api.capitalAction.getActionsDetails.useQuery(
    {
      capitalActionId: id,
      vehicleId,
      entryType: CapitalActionEntryType.Equalisation,
      actionIds: [equalisationId ?? ''],
    },
    {
      enabled: !!equalisationId,
    },
  );

  const equalisations =
    actionsDetails?.entryType === CapitalActionEntryType.Equalisation
      ? actionsDetails.actions
      : [];

  const isCapitalAndLoan =
    vehicle?.commitmentType === CommitmentType.CapitalAndLoan;

  const equalisationRows: Array<EqualisationRow> = equalisations.map(
    (equalisation) => ({
      id: equalisation.id,
      counterpartyLabel: equalisation.counterpartyLabel,
      closeDateLabel: equalisation.closeDate.toReadable(),
      numberOfNewCommitments: equalisation.numberOfNewCommitments,
      capitalContributionChange: numberToCurrencyString(
        equalisation.capitalContributionChangeLcy,
        { currency: baseCurrency },
      ),
      uncalledAmount: numberToCurrencyString(equalisation.uncalledAmountLcy, {
        currency: baseCurrency,
      }),
      allocationRows: equalisation.allocations.map((allocation, i) => ({
        id: String(i),
        investorLabel: allocation.investorLabel,
        netEqualisationPayableReceivable: numberToCurrencyString(
          allocation.netEqualisationPayableReceivable,
          { currency: baseCurrency, negativeInBrackets: true },
        ),
        totalEqualisationInterestAmountLcy: numberToCurrencyString(
          allocation.totalEqualisationInterestAmountLcy,
          { currency: baseCurrency, negativeInBrackets: true },
        ),
        totalFeesAmountLcy: numberToCurrencyString(
          allocation.totalFeesAmountLcy,
          { currency: baseCurrency },
        ),
        capitalContributionChangeLcy: numberToCurrencyString(
          allocation.capitalContributionChangeLcy,
          { currency: baseCurrency },
        ),
        capitalContributionUsedLcy: numberToCurrencyString(
          allocation.capitalContributionUsedLcy,
          { currency: baseCurrency },
        ),
        unpaidAllocationAmountLcy: numberToCurrencyString(
          allocation.unpaidAllocationAmountLcy,
          { currency: baseCurrency, negativeInBrackets: true },
        ),
      })),
      capitalContributionChangeRows: equalisation.selectedCommitments.map(
        (commitment, i) => ({
          id: String(i),
          investorName: commitment.investorName,
          originalCapital: numberToCurrencyString(
            commitment.existingCapitalCommitmentLcy,
            { fallback: '-', currency: baseCurrency },
          ),
          newCapital: numberToCurrencyString(commitment.capitalCommitmentLcy, {
            fallback: '-',
            currency: baseCurrency,
          }),
          change: numberToCurrencyString(commitment.capitalContributionChange, {
            fallback: '-',
            currency: baseCurrency,
            negativeInBrackets: true,
          }),
        }),
      ),
    }),
  );

  const handleSelectEqualisation = (equalisationId: string) => {
    const equalisation = availableActions.find(
      (action) => action.id === equalisationId,
    );
    if (equalisation) {
      setValue(
        `entries.${index}.totalAmountLcy`,
        equalisation.uncalledAmountLcy,
      );
    }
  };

  return (
    <Column spacing="lg">
      <FormField label="Select equalisation" size="md">
        <Controller
          name={`entries.${index}.equalisationId`}
          control={control}
          render={({
            fieldState: { error },
            field: { onChange, ...field },
          }) => (
            <BasicSelect
              placeholder="Select equalisation"
              fullWidth
              options={availableActions.map((action) => ({
                label: `Equalisation - ${action.ledgerDate.toReadable()}`,
                value: action.id,
              }))}
              onChange={(value) => {
                handleSelectEqualisation(value);
                onChange(value);
              }}
              {...field}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      {!!equalisationId && <Divider />}
      {!!equalisationId && (
        <Table
          rows={equalisationRows}
          columns={
            isCapitalAndLoan
              ? capitalAndLoanEqualisationColumns
              : baseEqualisationColumns
          }
          scrollable
          disableSorting
          isExpandableRow={() => true}
          isExpandedRow={(row) => expandedRows.has(row.id)}
          onRowExpand={(row) => toggleRowExpansion(row.id)}
          onRowClick={(row) => toggleRowExpansion(row.id)}
          getExpandedContent={(row) => (
            <ExpandedContent>
              <Column spacing="lg">
                {isCapitalAndLoan && (
                  <Table
                    title="Capital contribution changes"
                    rows={row.capitalContributionChangeRows}
                    columns={capitalContributionChangeColumns}
                    disableSorting
                    showHeaderBorder
                    scrollable
                  />
                )}
                <Table
                  title="Equalisation allocation"
                  rows={row.allocationRows}
                  columns={equalisationAllocationColumns}
                  disableSorting
                  showHeaderBorder
                  scrollable
                />
              </Column>
            </ExpandedContent>
          )}
        />
      )}
    </Column>
  );
};

export default EqualisationsDetails;
